import React, { useState, useEffect } from 'react'
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField,
  Typography
} from '@mui/material'
import { DesktopDatePicker } from '@mui/x-date-pickers'

import {
  previousDate,
  formatSsn,
  handleChange,
  datesBetween
} from '../../../services/formhelper.service'
import { FormState } from '../types'
import { DateProvider } from '../../DateProvider/DateProvider'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { ComponentState, PersonalInFormationFormProps } from './types'
import { MuiTextFieldProps } from '@mui/x-date-pickers/internals'
import moment from 'moment'

export const PersonalInformationComponent: React.FC<
  PersonalInFormationFormProps
> = ({ formData, setFormData }) => {
  const minAge = previousDate(18)
  const maxAge = previousDate(120)

  const [values, setValues] = useState<FormState<ComponentState>>({
    ...formData,
    showSnn: false,
    validationErrors: {}
  })

  useEffect(() => {
    // Enforce validation criteria before proceeding
    // i.e. save a valid value or reset to default so the next action is disabled
    setFormData({
      dob: values.validationErrors.dob ? null : values.dob,
      ssn: values.validationErrors.ssn ? '' : values.ssn,
      annualIncome: values.validationErrors.annualIncome
        ? 0
        : values.annualIncome && typeof values.annualIncome === 'string'
        ? parseFloat(values.annualIncome)
        : values.annualIncome || 0
    })
  }, [values])

  const handleClickShowSsn = () => {
    setValues({
      ...values,
      showSnn: !values.showSnn
    })
  }

  const handleMouseDownSsn = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const validate = (
    prop: string,
    value: string | number | Date
  ): Record<string, string | void> => {
    if (prop === 'ssn' && typeof value === 'string') {
      return {
        ...values.validationErrors,
        ssn:
          value.split('').filter((v) => v !== '-').length > 9
            ? 'invalid'
            : undefined
      }
    }
    if (prop === 'annualIncome') {
      return {
        ...values.validationErrors,
        annualIncome: value < 18000 ? 'invalid' : undefined
      }
    }
    if (prop === 'dob' && moment.isMoment(value)) {
      return {
        ...values.validationErrors,
        dob: datesBetween(minAge, maxAge, value) ? undefined : 'invalid'
      }
    }
    return { ...values.validationErrors }
  }

  const onChangeformatSsn = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = formatSsn(e.target.value)
    return setValues(
      handleChange<ComponentState>(
        'ssn',
        { ...values, validationErrors: validate('ssn', e.target.value) },
        e
      )
    )
  }

  const onBirthDateChange = (value: Date) => {
    return setValues({
      ...values,
      dob: value,
      validationErrors: validate('dob', value)
    })
  }

  return (
    <>
      <Typography component="div" variant="body1" align="left" gutterBottom>
        What is your date of birth?
      </Typography>

      <DateProvider>
        <DesktopDatePicker
          inputFormat="MM/DD/YYYY"
          value={values.dob}
          onChange={onBirthDateChange}
          maxDate={minAge}
          renderInput={(params: MuiTextFieldProps) => (
            <TextField
              {...params}
              sx={{ flex: 1, minWidth: 100 }}
              required
              placeholder="birth date"
              type="date"
              error={!!values.dob && !!values.validationErrors.dob}
              helperText={!!values.validationErrors.dob && 'Invalid Birthdate'}
            />
          )}
        />
      </DateProvider>

      <Typography
        component="div"
        variant="body1"
        align="left"
        gutterBottom
        sx={{ mt: 3 }}
      >
        What is your social security number?
      </Typography>

      <FormControl
        variant="outlined"
        required
        sx={{ minWidth: 250 }}
        error={!!values.ssn && !!values.validationErrors.ssn}
      >
        <OutlinedInput
          id="outlined-adornment-ssn"
          type={values.showSnn ? 'text' : 'password'}
          value={values.ssn}
          onChange={onChangeformatSsn}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle ssn visibility"
                onClick={handleClickShowSsn}
                onMouseDown={handleMouseDownSsn}
                edge="end"
              >
                {values.showSnn ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          placeholder="Social Security number"
        />
        {!!values.ssn && !!values.validationErrors.ssn && (
          <FormHelperText id="outlined-adornment-ssn">
            9 characters max
          </FormHelperText>
        )}
        {!values.validationErrors.ssn && (
          <FormHelperText id="outlined-adornment-ssn">
            Your social security number will only be used for identity
            verification purposes
          </FormHelperText>
        )}
      </FormControl>

      <Typography
        component="div"
        variant="body1"
        align="left"
        gutterBottom
        sx={{ mt: 3 }}
      >
        What is your annual income?
      </Typography>

      <FormControl
        variant="outlined"
        required
        sx={{ minWidth: 250 }}
        error={!!values.annualIncome && !!values.validationErrors.annualIncome}
      >
        <OutlinedInput
          id="adornment-annualIncome"
          value={values.annualIncome || ''}
          type="number"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setValues(
              handleChange<ComponentState>(
                'annualIncome',
                {
                  ...values,
                  validationErrors: validate('annualIncome', e.target.value)
                },
                e
              )
            )
          }
          startAdornment={<InputAdornment position="start">$</InputAdornment>}
          placeholder="Total Annual Income"
        />
        {!!values.annualIncome && !!values.validationErrors.annualIncome && (
          <FormHelperText id="outlined-adornment-ssn">
            Must be greater than $18,000
          </FormHelperText>
        )}
      </FormControl>

      <Typography variant="subtitle2" component="div" sx={{ mt: 2 }}>
        Income includes (but is not limited to):
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexFlow: 'row wrap',
          justifyContent: 'flex-start',
          alignContent: 'space-around',
          rowGap: 2,
          columnGap: 2,
          mb: 2
        }}
      >
        <Typography
          component="div"
          variant="caption"
          sx={{ flex: 1, minWidth: 150 }}
        >
          &#8226; Your yearly salary, wages, commissions and tips before taxes
        </Typography>
        <Typography
          component="div"
          variant="caption"
          sx={{ flex: 1, minWidth: 150 }}
        >
          &#8226; Government benefits, public assistance, retirement benefits,
          investments, and rent paid to you
        </Typography>
        <Typography
          component="div"
          variant="caption"
          sx={{ flex: 1, minWidth: 150 }}
        >
          &#8226; Alimony, child support or separate maintenance income (not
          required unless you want it considered as a basis for repayment)
        </Typography>
      </Box>
    </>
  )
}
